import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { DateFormats, WeekDays, useDateTimeFormat } from '../hooks';
import { Translations, currentEnvAppConfig, locale } from '../utils';

export type LanguageKey = 'en' | 'sv' | 'no';

export type GetTranslation = (
  key: string,
  options?: object,
  fallbackValue?: string
) => string;

const ENV_DEFAULT_LOCALE = currentEnvAppConfig.defaultLocale as LanguageKey;

interface LocaleContextType {
  t: GetTranslation;
  currentLanguage: LanguageKey;
  setCurrentLanguage: Dispatch<SetStateAction<LanguageKey>>;
  formatDate: (date: Date | number, customFormat?: string) => string;
  formatTime: (date: Date | number, customFormat?: string) => string;
  formatDateTime: (date: Date | number, customFormat?: string) => string;
  formatDateTimeOrFallback: (value: string | null | undefined, fallback?: string, customFormat?: string) => string;
  formatDateOrFallback: (value: string | null | undefined, fallback?: string, customFormat?: string) => string;
  formatTimeOrFallback: (value: string | null | undefined, fallback?: string, customFormat?: string) => string;
  dateFormat: string;
  timeFormat: string;
  dateTimeFormat: string;
  weekDays: WeekDays;
  dateFormats: DateFormats;
}

export const LocaleContext = React.createContext<LocaleContextType>(
  {} as LocaleContextType
);

interface LocaleProviderProps extends PropsWithChildren {
  translations: Translations;
  defaultLanguage?: LanguageKey;
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({
  children,
  translations,
  defaultLanguage = ENV_DEFAULT_LOCALE,
}) => {
  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage);

  const t = useCallback(
    (key: string, options?: object, fallbackValue?: string) =>
      locale(key, currentLanguage, options, fallbackValue, translations),
    [currentLanguage, translations]
  );

  const {
    formatDate,
    formatTime,
    dateFormat,
    timeFormat,
    weekDays,
    formatDateTime,
    formatDateTimeOrFallback,
    formatDateOrFallback,
    formatTimeOrFallback,
    dateTimeFormat,
    dateFormats,
  } = useDateTimeFormat(currentLanguage);

  const value = useMemo(
    () => ({
      t,
      currentLanguage,
      setCurrentLanguage,
      formatDate,
      formatTime,
      formatDateTime,
      formatDateTimeOrFallback,
      formatDateOrFallback,
      formatTimeOrFallback,
      dateFormat,
      timeFormat,
      dateTimeFormat,
      weekDays,
      dateFormats,
    }),
    [
      t,
      currentLanguage,
      formatDate,
      formatTime,
      formatDateTime,
      formatDateTimeOrFallback,
      formatDateOrFallback,
      formatTimeOrFallback,
      dateFormat,
      timeFormat,
      dateTimeFormat,
      weekDays,
      dateFormats,
    ]
  );

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => React.useContext(LocaleContext);
